import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { toast } from 'react-hot-toast';

export interface BotPersonaConfig {
  genero: 'feminino' | 'masculino';
  tom: 'amigável' | 'profissional' | 'casual' | 'vendedor' | 'suporte';
  nomeBot: string;
  avatarUrl?: string; // URL ou base64 da foto do bot
  giriasPermitidas: string[];
  abreviacoesPermitidas: string[];
  nivelFormalidade: number; // 0-10
  usarHumor: boolean;
  templateSystemPrompt: string;
}

/**
 * Hook para gerenciar configuração de persona do bot
 * ✅ 100% DINÂMICO - Carrega/salva via API
 */
export const usePersonaConfig = (userId: string | null) => {
  const queryClient = useQueryClient();

  // Query para carregar config
  const { data: persona, isLoading, error, dataUpdatedAt } = useQuery<BotPersonaConfig>({
    queryKey: ['persona-config', userId],
    queryFn: async () => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[usePersonaConfig] Carregando persona do backend...');
      const response = await api.get(`/api/config/persona/${userId}`);
      console.log('[usePersonaConfig] ✅ Persona carregada do backend:', response.data);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 min
    // ✅ Garantir que refetch após reset realmente busque dados novos
    refetchOnMount: true,
  });

  // Mutation para salvar config
  const savePersona = useMutation({
    mutationFn: async (config: BotPersonaConfig) => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[usePersonaConfig] Salvando persona:', config);
      const response = await api.post(`/api/config/persona/${userId}`, config);
      return response.data;
    },
    onSuccess: () => {
      // Invalida apenas configs de persona (NÃO scripts!)
      // Scripts só devem ser regenerados quando explicitamente solicitado
      queryClient.invalidateQueries({ queryKey: ['persona-config', userId] });

      // 🔥 Feedback de sync em tempo real
      toast.success('✅ Salvo! Bot já está usando as novas configurações', {
        duration: 3000,
        icon: '⚡',
      });
    },
    onError: (error: any) => {
      console.error('[usePersonaConfig] Erro ao salvar:', error);
      toast.error('❌ Erro ao salvar persona: ' + (error.response?.data?.message || error.message));
    },
  });

  // Mutation para resetar para padrão
  const resetPersona = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('userId é obrigatório');

      const response = await api.delete(`/api/config/persona/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona-config', userId] });
      toast.success('✅ Persona resetada para padrão!');
    },
    onError: (error: any) => {
      toast.error('❌ Erro ao resetar: ' + (error.response?.data?.message || error.message));
    },
  });

  return {
    persona: persona ?? createDefaultPersona(),
    isLoading,
    error,
    savePersona: savePersona.mutate,
    resetPersona: resetPersona.mutate,
    isSaving: savePersona.isPending,
    isResetting: resetPersona.isPending,
  };
};

/**
 * Cria config padrão local (fallback)
 * ✅ Nome do bot é a FONTE ÚNICA - usado em todo sistema
 */
function createDefaultPersona(): BotPersonaConfig {
  return {
    genero: 'feminino',
    tom: 'amigável',
    nomeBot: 'Assistente Virtual', // 🎯 FONTE ÚNICA para nome do bot
    giriasPermitidas: ['amo', 'super', 'incrível', 'demais'],
    abreviacoesPermitidas: ['vc', 'tbm', 'pq', 'td'],
    nivelFormalidade: 3,
    usarHumor: true,
    templateSystemPrompt: 'Você é {nomeBot}, uma atendente virtual {genero} com tom {tom}.',
  };
}
